import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaEdit, FaTrash, FaClone, FaChartBar, FaPlus, FaEllipsisV } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { budgetService } from '../../../services/apiService';
import { createPortal } from 'react-dom';
import BudgetStatsDashboard from './BudgetStatsDashboard';

const BudgetList = () => {
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeMenu, setActiveMenu] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const navigate = useNavigate();

  useEffect(() => {
    const fetchBudgets = async () => {
      try {
        setLoading(true);
        const data = await budgetService.getAllBudgets();
        setBudgets(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error fetching budgets:', error);
        toast.error('Failed to load budgets');
        setBudgets([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBudgets();
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this budget?')) {
      try {
        await budgetService.deleteBudget(id);
        setBudgets(budgets.filter(budget => budget._id !== id));
        toast.success('Budget deleted');
      } catch (error) {
        console.error('Error deleting budget:', error);
        toast.error('Failed to delete budget');
      }
    }
  };

  const handleClone = async (id) => {
    try {
      const clonedBudget = await budgetService.cloneBudget(id);
      setBudgets([...budgets, clonedBudget]);
      toast.success('Budget cloned successfully');
    } catch (error) {
      console.error('Error cloning budget:', error);
      toast.error('Failed to clone budget');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const toggleMenu = (budgetId, event) => {
    if (event) {
      const rect = event.currentTarget.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + window.scrollY,
        left: rect.right - 192, // 192px is the menu width (w-48)
      });
    }
    setActiveMenu(activeMenu === budgetId ? null : budgetId);
  };

  const ActionMenu = ({ budget }) => (
    <>
      <button
        onClick={(e) => {
          e.stopPropagation();
          toggleMenu(budget._id, e);
        }}
        className="p-2 text-gray-600 hover:text-gray-800 bg-gray-50 hover:bg-gray-100 rounded-lg transition-all duration-200"
        title="More actions"
      >
        <FaEllipsisV size={16} />
      </button>
      
      {activeMenu === budget._id && createPortal(
        <>
          <div 
            className="fixed inset-0 z-40 bg-black bg-opacity-5"
            onClick={() => toggleMenu(null)}
          />
          <div 
            className="fixed z-50 w-48 bg-white rounded-lg shadow-xl py-1 border border-gray-200"
            style={{
              top: `${menuPosition.top}px`,
              left: `${menuPosition.left}px`,
            }}
          >
            <Link 
              to={`/budgeting/${budget._id}/edit`}
              className="flex items-center px-4 py-2 text-sm bg-white text-indigo-600 hover:text-indigo-700"
            >
              <FaEdit size={16} className="mr-2 text-indigo-600" /> Edit
            </Link>
            <Link 
              to={`/budgeting/${budget._id}/actual`}
              className="flex items-center px-4 py-2 text-sm bg-white text-blue-600 hover:text-blue-700"
            >
              <FaChartBar size={16} className="mr-2 text-blue-600" /> View Actual
            </Link>
            <button 
              onClick={(e) => {
                e.stopPropagation();
                handleClone(budget._id);
                toggleMenu(null);
              }}
              className="w-full flex items-center px-4 py-2 text-sm bg-white text-purple-600 hover:text-purple-700"
            >
              <FaClone size={16} className="mr-2 text-purple-600" /> Clone
            </button>
            <button 
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(budget._id);
                toggleMenu(null);
              }}
              className="w-full flex items-center px-4 py-2 text-sm bg-white text-rose-600 hover:text-rose-700"
            >
              <FaTrash size={16} className="mr-2 text-rose-600" /> Delete
            </button>
          </div>
        </>,
        document.body
      )}
    </>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <BudgetStatsDashboard />
      
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-semibold text-gray-800">My Budgets</h1>
        <Link 
          to="/budgeting/new" 
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
        >
          <FaPlus size={14} />
          <span className="hidden sm:inline">Create New Budget</span>
          <span className="sm:hidden">New</span>
        </Link>
      </div>

      {budgets.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center border border-gray-100">
          <div className="max-w-md mx-auto">
            <h3 className="text-xl font-medium text-gray-800 mb-3">No Budgets Yet</h3>
            <p className="text-gray-600 mb-6">Create your first budget to start tracking your income and expenses.</p>
            <Link 
              to="/budgeting/new"
              className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
            >
              <FaPlus size={14} />
              Create Your First Budget
            </Link>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="w-full overflow-x-auto">
            <table className="w-full table-auto">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="py-3 px-3 text-left text-xs font-medium text-gray-600 whitespace-nowrap">
                    Name
                  </th>
                  <th scope="col" className="py-3 px-3 text-left text-xs font-medium text-gray-600 hidden sm:table-cell whitespace-nowrap">
                    Period
                  </th>
                  <th scope="col" className="py-3 px-3 text-left text-xs font-medium text-gray-600 hidden md:table-cell whitespace-nowrap">
                    Est. Net Income
                  </th>
                  <th scope="col" className="py-3 px-3 text-left text-xs font-medium text-gray-600 hidden md:table-cell whitespace-nowrap">
                    Expenses
                  </th>
                  <th scope="col" className="py-3 px-3 text-left text-xs font-medium text-gray-600 hidden lg:table-cell whitespace-nowrap">
                    Balance
                  </th>
                  <th scope="col" className="py-3 px-3 text-left text-xs font-medium text-gray-600 hidden lg:table-cell whitespace-nowrap">
                    Status
                  </th>
                  <th scope="col" className="py-3 px-3 text-right text-xs font-medium text-gray-600 whitespace-nowrap">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {budgets.map((budget) => (
                  <tr key={budget._id} className="hover:bg-gray-50">
                    <td className="py-3 px-3 whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        <Link to={`/budgeting/${budget._id}`} className="text-indigo-600 hover:text-indigo-800 font-medium text-sm">
                          {budget.name}
                        </Link>
                        <div className="flex flex-wrap gap-1">
                          {budget.isPrimary && (
                            <span className="bg-emerald-50 text-emerald-700 text-xs px-2 py-0.5 rounded-full font-medium">Primary</span>
                          )}
                          {budget.isScenario && (
                            <span className="bg-purple-50 text-purple-700 text-xs px-2 py-0.5 rounded-full font-medium">Scenario</span>
                          )}
                        </div>
                        <div className="sm:hidden text-xs text-gray-600">
                          {formatDate(budget.period.startDate)} - {formatDate(budget.period.endDate)}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-3 text-xs text-gray-600 hidden sm:table-cell whitespace-nowrap">
                      {formatDate(budget.period.startDate)} - {formatDate(budget.period.endDate)}
                      <div className="text-xs text-gray-500 mt-1">
                        {budget.period.type.charAt(0).toUpperCase() + budget.period.type.slice(1)}
                      </div>
                    </td>
                    <td className="py-3 px-3 text-xs text-gray-900 font-medium hidden md:table-cell whitespace-nowrap">
                      ${budget.totalNetIncome?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || 
                        (budget.income?.reduce((sum, inc) => sum + (inc.netAmount || inc.plannedAmount * 0.75), 0))?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      {budget.totalEstimatedTaxes > 0 && (
                        <span className="text-xs text-gray-500 block">
                          Est. Tax: ${budget.totalEstimatedTaxes.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-3 text-xs text-gray-900 font-medium hidden md:table-cell whitespace-nowrap">
                      ${budget.totalPlannedExpenses?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="py-3 px-3 text-xs hidden lg:table-cell whitespace-nowrap">
                      <span className={`font-medium ${(budget.totalNetIncome || budget.income?.reduce((sum, inc) => sum + (inc.netAmount || inc.plannedAmount * 0.75), 0)) - budget.totalPlannedExpenses >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                        ${((budget.totalNetIncome || budget.income?.reduce((sum, inc) => sum + (inc.netAmount || inc.plannedAmount * 0.75), 0)) - budget.totalPlannedExpenses)?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-xs hidden lg:table-cell whitespace-nowrap">
                      {budget.isActive ? (
                        <span className="bg-emerald-50 text-emerald-700 text-xs px-2 py-0.5 rounded-full font-medium">Active</span>
                      ) : (
                        <span className="bg-gray-50 text-gray-700 text-xs px-2 py-0.5 rounded-full font-medium">Inactive</span>
                      )}
                    </td>
                    <td className="py-3 px-3 text-right whitespace-nowrap">
                      <ActionMenu budget={budget} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default BudgetList; 